import { FormattedMessage, useIntl } from 'react-intl'
import { useState, useEffect, useCallback } from 'react'
import { useDialog } from '@hooks/useDialog'

import './ShareDialog.css'

interface ShareDialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  text?: string
  url?: string
}

const canShare = typeof navigator !== 'undefined' && !!navigator.share

export default function ShareDialog({
  isOpen,
  onClose,
  title,
  text,
  url
}: ShareDialogProps) {
  const { dialogRef, handleBackdropClick } = useDialog(isOpen, onClose)
  const intl = useIntl()
  const [copied, setCopied] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isOpen) setCopied(false)
  }, [isOpen])

  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])

  const shareUrl =
    url ?? (typeof window !== 'undefined' ? window.location.href : '')

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
    } catch (error) {
      console.error('Error al copiar:', error)
    }
  }, [shareUrl])

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share({ title, text, url: shareUrl })
    } catch (error) {
      if ((error as DOMException).name !== 'AbortError') {
        console.error('Error al compartir:', error)
      }
    }
  }, [title, text, shareUrl])

  return (
    <dialog
      id="share-dialog"
      className="app-dialog"
      ref={dialogRef}
      onClick={handleBackdropClick}
      onCancel={onClose}
    >
      <div className="dialog-inner">
        <h3>
          <FormattedMessage id="share-dialog.title" />
        </h3>
        <div className="share-link-row">
          <textarea
            readOnly
            value={shareUrl}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={intl.formatMessage({ id: 'share-dialog.link-label' })}
          />
          <button className="highlight-button" onClick={handleCopy}>
            <span className="icon icon-copy" />
            <FormattedMessage
              id={copied ? 'share-dialog.copied' : 'share-dialog.copy'}
            />
          </button>
        </div>
        {isMounted && canShare && (
          <button
            className="standart-button share-native"
            onClick={handleNativeShare}
          >
            <span className="icon icon-share" />
            <FormattedMessage id="share-dialog.native" />
          </button>
        )}
      </div>
    </dialog>
  )
}
